import { useIntl } from 'react-intl';
import { ActivityAction, ActivityItem } from 'app/data/types/activity';
import { ACTION_LABEL_ID } from 'app/shared/consts/activity';
import { formatTimeRangeLabel, formatWhen, formatWhere, fromNowLabel } from 'app/shared/utils/format-activity';

export interface ActivityCardCopy {
  isOpen: boolean;
  actionNeeded: boolean;
  rateable: boolean;
  /** "Today · 2:30 pm" for scheduled items. */
  whenText: string;
  /** Composed subtitle incl. localized "Seats {n}" and visitor host framing. */
  where: string;
  /** "Updated 2 hours ago" / "Created 2 days ago" for the open-request footer. */
  updatedMeta: string;
  primaryAction?: ActivityAction;
  bookAgainAction?: ActivityAction;
  actionLabel: (action: ActivityAction) => string;
}

/** Composes all localized card copy — the FE's text work (BE ships raw values only). */
export const useActivityCardCopy = (item: ActivityItem): ActivityCardCopy => {
  const intl = useIntl();
  const { details } = item;

  // A request is open-ended by nature: nothing is scheduled, so it takes the same heading and footer as
  // an open item rather than a "when" line. The BE only ever set `flags.open` on mocked data.
  const isTask = item.type === 'work_order';

  let where = formatWhere(item);
  if (details.seats) {
    const seats = intl.formatMessage({ id: 'activity.where.seats' }, { n: details.seats });
    where = where ? `${where} · ${seats}` : seats;
  }
  // Visitors show the visit LOCATION (translated "Floor {n}, Suite {n}"), not the host framing —
  // it's always the host, so the label was noise (Lis, MBX-950).
  if (item.type === 'visitor_registration') {
    const parts: string[] = [];
    if (details.floor) parts.push(intl.formatMessage({ id: 'activity.where.floor' }, { n: details.floor }));
    if (details.suite) parts.push(intl.formatMessage({ id: 'activity.where.suite' }, { n: details.suite }));
    // A visit with no floor recorded rendered no `where` at all; the building is coarse but true.
    where = parts.join(', ') || details.buildingName || '';
  }

  // Building-local time values, user-locale formatting (RES-741). A visit carries an end → render a range.
  const when = formatWhen(item.timestamp, item.context.buildingTimezone, undefined, intl.locale);
  const whenLabel = when.dayKey === 'date' ? when.dateLabel : intl.formatMessage({ id: `activity.day.${when.dayKey}` });
  const timeText = item.endTimestamp
    ? formatTimeRangeLabel(item.timestamp, item.endTimestamp, item.context.buildingTimezone, intl.locale)
    : when.timeLabel;
  const whenText = item.timestamp ? `${whenLabel} · ${timeText}` : '';

  // "Updated" only once something actually happened. Both dates are sent whether or not they differ, so
  // the wording is decided here by comparing them — an untouched request reads "Created 2 days ago"
  // rather than reporting the moment it was raised as an update.
  const relative = (iso: string) => fromNowLabel(iso, undefined, intl.locale);
  const changedSinceCreated = !!details.updatedAt && details.updatedAt !== details.createdAt;

  let updatedMeta = '';
  if (details.updatedAt && changedSinceCreated) {
    updatedMeta = intl.formatMessage({ id: 'activity.meta.updated' }, { time: relative(details.updatedAt) });
  } else if (details.createdAt) {
    updatedMeta = intl.formatMessage({ id: 'activity.meta.created' }, { time: relative(details.createdAt) });
  } else if (isTask && item.timestamp) {
    // Projected before the dates were sent; the activity time is the last-changed time for a task.
    updatedMeta = intl.formatMessage({ id: 'activity.meta.updated' }, { time: relative(item.timestamp) });
  }

  const actionLabel = (action: ActivityAction): string => {
    const id = ACTION_LABEL_ID[action.type];
    return id ? intl.formatMessage({ id }) : action.type.replace(/[_-]+/g, ' ');
  };

  return {
    isOpen: isTask || Boolean(details.flags?.open),
    actionNeeded: Boolean(details.flags?.actionNeeded),
    rateable: Boolean(details.flags?.rateable),
    whenText,
    where,
    updatedMeta,
    primaryAction: item.actions?.[0],
    bookAgainAction: item.actions?.find((a) => a.type === 'book_again'),
    actionLabel,
  };
};
